/**
 * Calendar Screen Manager
 *
 * Business logic manager for calendar screen operations.
 * Handles permissions, loading calendars/events, and data management.
 *
 * Follows manager pattern: centralizes business logic separate from UI.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { fetch as expoFetch } from 'expo/fetch';

import type { CalendarEvent } from '@/types/events';
import type { AgendaSection, AttendeeColorMap, LinkedCalendarMap } from '@/types/calendar-ui';
import { deviceCalendarService, type DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { canAccessDeviceCalendar } from '@/lib/calendar/platform';
import { CalendarEventFormatter } from '@/lib/calendar/CalendarEventFormatter';
import { generateAPIUrl } from '@/utils';

const STORAGE_KEY_SELECTED_CALENDARS = 'selectedCalendarIds';

type PermissionStatus = 'granted' | 'denied' | 'undetermined';

type CalendarScreenState = {
  hasPermission: boolean | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  deviceCalendars: DeviceCalendar[];
  selectedCalendarIds: string[];
  sections: AgendaSection[];
};

/**
 * CalendarScreenManager
 *
 * Manages all business logic for the calendar screen including:
 * - Permission management
 * - Calendar and event loading
 * - Tracked members management
 * - State persistence
 */
export class CalendarScreenManager {
  private accessToken: string | null;

  constructor(accessToken: string | null = null) {
    this.accessToken = accessToken;
  }

  /**
   * Update access token for API requests
   */
  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  /**
   * Check if device calendar access is available
   */
  canAccessCalendar(): boolean {
    return canAccessDeviceCalendar();
  }

  /**
   * Check calendar permission status
   */
  async checkPermissionStatus(): Promise<PermissionStatus> {
    if (!this.canAccessCalendar()) {
      return 'denied';
    }

    const { status } = await deviceCalendarService.checkPermissions();
    return status;
  }

  /**
   * Request calendar permissions
   */
  async requestPermissions(): Promise<{
    status: PermissionStatus;
    error?: string;
  }> {
    try {
      const { status } = await deviceCalendarService.requestPermissions();

      if (status === 'granted') {
        return { status: 'granted' };
      } else {
        return {
          status: 'denied',
          error: 'Calendar permission was denied',
        };
      }
    } catch (err) {
      return {
        status: 'denied',
        error: 'Failed to request calendar permission',
      };
    }
  }

  /**
   * Load device calendars
   */
  async loadDeviceCalendars(): Promise<DeviceCalendar[]> {
    try {
      const calendars = await deviceCalendarService.getDeviceCalendars();
      return calendars;
    } catch (err) {
      console.error('[CalendarScreenManager] Failed to load calendars:', err);
      throw new Error('Failed to load device calendars');
    }
  }

  /**
   * Load saved calendar selection from storage
   * Returns valid calendar IDs or auto-selects primary calendar
   */
  async loadCalendarSelection(
    availableCalendars: DeviceCalendar[],
  ): Promise<string[]> {
    try {
      const savedSelection = await AsyncStorage.getItem(
        STORAGE_KEY_SELECTED_CALENDARS,
      );

      if (savedSelection) {
        const saved = JSON.parse(savedSelection) as string[];
        // Filter to only valid calendar IDs
        const validIds = saved.filter((id) =>
          availableCalendars.some((cal) => cal.id === id),
        );
        if (validIds.length > 0) {
          return validIds;
        }
      }

      // Auto-select primary calendar
      const primary = availableCalendars.find((c) => c.isPrimary);
      if (primary) {
        return [primary.id];
      } else if (availableCalendars.length > 0) {
        return [availableCalendars[0].id];
      }

      return [];
    } catch (err) {
      console.error(
        '[CalendarScreenManager] Failed to load calendar selection:',
        err,
      );
      return [];
    }
  }

  /**
   * Save calendar selection to storage
   */
  async saveCalendarSelection(selectedIds: string[]): Promise<void> {
    try {
      await AsyncStorage.setItem(
        STORAGE_KEY_SELECTED_CALENDARS,
        JSON.stringify(selectedIds),
      );
    } catch (err) {
      console.error(
        '[CalendarScreenManager] Failed to save calendar selection:',
        err,
      );
    }
  }

  /**
   * Load events from device for selected calendars AND task-backed events
   */
  async loadEvents(
    selectedCalendarIds: string[],
    options?: { startDate?: Date; endDate?: Date; useMinimalFields?: boolean },
  ): Promise<{ sections: AgendaSection[]; flatItems: import('@/types/calendar-ui').FlatAgendaItem[]; eventsCount: number }> {
    if (selectedCalendarIds.length === 0) {
      return { sections: [], flatItems: [], eventsCount: 0 };
    }

    try {
      const now = new Date();
      const defaultStart = new Date(now.getFullYear(), now.getMonth() - 3, 1);
      const defaultEnd = new Date(now.getFullYear(), now.getMonth() + 12, 0);
      const startDate = options?.startDate ?? defaultStart;
      const endDate = options?.endDate ?? defaultEnd;
      const useMinimalFields = options?.useMinimalFields ?? false;

      // Load device events
      const deviceEvents = await deviceCalendarService.listEvents(
        selectedCalendarIds,
        startDate,
        endDate,
      );

      const allEvents = deviceEvents;

      // Build both formats: sections for FamilyColumnView, flatItems for CalendarAgendaView
      const sections = CalendarEventFormatter.buildAgendaSections(allEvents);
      const flatItems = CalendarEventFormatter.buildFlatAgendaList(allEvents);

      return { sections, flatItems, eventsCount: allEvents.length };
    } catch (err) {
      console.error('[CalendarScreenManager] Failed to load events:', err);
      throw new Error((err as Error).message || 'Failed to load events');
    }
  }

  /**
   * Build attendee color map from current user
   */
  buildAttendeeColorMap(
    secondaryColor: string,
    currentUserProfile?: { email?: string | null; profileColor?: string | null; name: string } | null,
  ): AttendeeColorMap {
    const map = new Map<
      string,
      { color: string; name: string; status: string | null }
    >();

    // Add current user to the map
    if (currentUserProfile?.email) {
      map.set(currentUserProfile.email.toLowerCase(), {
        color: currentUserProfile.profileColor ?? secondaryColor,
        name: currentUserProfile.name,
        status: null,
      });
    }

    return map;
  }

  /**
   * Build linked calendar map from current user
   */
  buildLinkedCalendarMap(
    secondaryColor: string,
    currentUserProfile?: { linkedCalendars?: { id: string }[] | null; profileColor?: string | null; name: string } | null,
  ): LinkedCalendarMap {
    const map = new Map<string, { color: string; name: string }>();

    // Add current user's linked calendars
    if (currentUserProfile?.linkedCalendars) {
      currentUserProfile.linkedCalendars.forEach((cal) => {
        map.set(cal.id, {
          color: currentUserProfile.profileColor ?? secondaryColor,
          name: currentUserProfile.name,
        });
      });
    }

    return map;
  }

  /**
   * Ensure selected date is valid within sections
   * Returns first section date if current date is not found
   */
  ensureValidSelectedDate(
    currentDate: string,
    sections: AgendaSection[],
  ): string {
    if (sections.some((section) => section.title === currentDate)) {
      return currentDate;
    }
    return (
      sections[0]?.title ?? CalendarEventFormatter.getTodayDateString()
    );
  }

}
